import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, Search, Bookmark, X, HandHeart, ArrowRight } from 'lucide-react';
import { http } from '@/lib/api';

function ProgressBar({ pct }) {
  return (
    <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
      <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function CourseCard({ c, onOpen, testid }) {
  const pct = c.progress?.pct ?? 0;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onOpen}
      className="group text-left bg-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col w-full h-full"
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
          <GraduationCap size={20} className="text-gold" strokeWidth={1.5} />
        </div>
        {c.saved ? <Bookmark size={16} className="text-gold fill-gold" /> : null}
      </div>
      <p className="font-heading text-lg text-navy mt-4 leading-snug">{c.title}</p>
      {c.subtitle ? <p className="text-sm text-navy/60 mt-1.5 leading-relaxed flex-1">{c.subtitle}</p> : <div className="flex-1" />}
      <p className="text-[11px] text-navy/40 mt-3">{c.lesson_count} lessons · {c.est_minutes} min</p>
      {pct > 0 ? (
        <div className="mt-3">
          <ProgressBar pct={pct} />
          <p className="text-[11px] text-navy/50 mt-1.5">{pct === 100 ? 'Completed' : `${pct}% complete`}</p>
        </div>
      ) : null}
    </button>
  );
}

export default function SupportUniversity() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cont, setCont] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [topic, setTopic] = useState(null);
  const [rel, setRel] = useState(null);
  const [lessonHits, setLessonHits] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'guides' ? 'guides' : 'courses');
  const [guides, setGuides] = useState([]);

  const changeTab = (t) => {
    setTab(t);
    setSearchParams(t === 'guides' ? { tab: 'guides' } : {}, { replace: true });
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (topic) params.append('topic', topic);
    if (rel) params.append('relationship', rel);
    const qs = params.toString();
    Promise.all([
      http.get(`/support/university/catalog${qs ? `?${qs}` : ''}`),
      http.get('/support/university/continue').catch(() => ({ data: [] })),
    ]).then(([cat, c]) => { setData(cat.data); setCont(Array.isArray(c.data) ? c.data : []); })
      .catch(() => {}).finally(() => setLoading(false));
    if (q && q.trim().length >= 2) {
      http.get(`/support/university/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setLessonHits(r.data?.lessons || [])).catch(() => setLessonHits([]));
    } else {
      setLessonHits([]);
    }
  }, [q, topic, rel]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { http.get('/support/guides').then((r) => setGuides(r.data || [])).catch(() => {}); }, []);

  const openCourse = (c, fromRec = false) => {
    if (fromRec) http.post('/support/university/events', { type: 'recommendation_click', course_id: c.id }).catch(() => {});
    nav(`/app/support-university/${c.id}`);
  };

  if (loading && !data) {
    return <div className="p-6 lg:px-10 lg:pt-10"><div className="animate-pulse h-40 bg-white rounded-3xl" /></div>;
  }

  const hasFilter = !!(q || topic || rel);
  const recommended = data?.recommended || [];
  const catalog = data?.catalog || [];
  const topics = data?.topics || [];
  const relationships = data?.relationships || [];
  const ctxLabel = relationships.find((r) => r.id === data?.support_context)?.label?.toLowerCase();

  return (
    <div className="p-6 lg:px-10 lg:pt-10 pb-24 space-y-8" data-testid="support-university">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">Brotherhood Support</p>
        <h1 className="font-heading text-4xl lg:text-6xl mt-2 leading-[1.05] text-navy">Support University</h1>
        <p className="font-heading text-xl lg:text-2xl italic text-navy/60 mt-2">Learn how to show up for the man you care about.</p>
      </div>

      {/* Courses | Guides toggle */}
      <div className="flex gap-6 border-b border-black/10">
        <button
          type="button"
          data-testid="su-tab-courses"
          onClick={() => changeTab('courses')}
          className={`pb-3 -mb-px text-sm font-semibold border-b-2 transition-smooth ${tab === 'courses' ? 'text-navy border-gold' : 'text-navy/45 border-transparent hover:text-navy/70'}`}
        >Courses</button>
        <button
          type="button"
          data-testid="su-tab-guides"
          onClick={() => changeTab('guides')}
          className={`pb-3 -mb-px text-sm font-semibold border-b-2 transition-smooth ${tab === 'guides' ? 'text-navy border-gold' : 'text-navy/45 border-transparent hover:text-navy/70'}`}
        >Guides</button>
      </div>

      {tab === 'guides' ? (
        <section data-testid="su-guides">
          <h2 className="font-heading text-lg text-navy mb-1">Support Guides</h2>
          <p className="text-sm text-navy/50 mb-4">Practical, evidence-informed guides for partners, parents, family, mentors, and friends.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {guides.map((g) => (
              <button
                key={g.id}
                type="button"
                data-testid={`su-guide-${g.id}`}
                onClick={() => nav(`/app/support/${g.id}`)}
                className="group text-left bg-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col w-full h-full"
              >
                <div className="w-11 h-11 rounded-2xl bg-beige flex items-center justify-center mb-4">
                  <HandHeart size={20} className="text-navy" strokeWidth={1.5} />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">Guide</p>
                <p className="font-heading text-lg text-navy leading-snug mt-1.5">{g.title}</p>
                {g.subtitle ? <p className="text-sm text-navy/60 leading-relaxed mt-2 flex-1">{g.subtitle}</p> : <div className="flex-1" />}
                <p className="text-xs text-navy/40 mt-4 uppercase tracking-widest inline-flex items-center gap-1">Read guide <ArrowRight size={12} /></p>
              </button>
            ))}
          </div>
        </section>
      ) : (
      <>
      {/* Search + filters */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 bg-white rounded-2xl px-4 h-12 card-soft max-w-xl">
          <Search size={18} className="text-navy/40" />
          <input
            data-testid="su-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses"
            className="flex-1 bg-transparent outline-none text-navy text-sm"
          />
          {q ? <button onClick={() => setQ('')} data-testid="su-search-clear"><X size={16} className="text-navy/40" /></button> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {topics.map((t) => (
            <button
              key={t.id}
              data-testid={`su-topic-${t.id}`}
              onClick={() => setTopic(topic === t.id ? null : t.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-smooth ${topic === t.id ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-black/10 hover:border-navy/30'}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {relationships.map((r) => (
            <button
              key={r.id}
              data-testid={`su-rel-${r.id}`}
              onClick={() => setRel(rel === r.id ? null : r.id)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-smooth ${rel === r.id ? 'bg-gold text-white border-gold' : 'bg-white text-navy/70 border-black/10 hover:border-navy/30'}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {/* Continue learning */}
      {!hasFilter && cont.length > 0 ? (
        <section>
          <h2 className="font-heading text-lg text-navy mb-3">Continue learning</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cont.map((c) => <CourseCard key={c.id} c={c} testid={`su-continue-${c.id}`} onOpen={() => openCourse(c)} />)}
          </div>
        </section>
      ) : null}

      {/* Recommended */}
      {!hasFilter && recommended.length > 0 ? (
        <section>
          <h2 className="font-heading text-lg text-navy">Recommended for you</h2>
          {ctxLabel ? <p className="text-sm text-navy/50 mb-3">Because you&rsquo;re supporting {ctxLabel}</p> : <div className="mb-3" />}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommended.map((c) => <CourseCard key={c.id} c={c} testid={`su-rec-${c.id}`} onOpen={() => openCourse(c, true)} />)}
          </div>
        </section>
      ) : null}

      {/* Lesson matches (deep search) */}
      {q && q.trim().length >= 2 && lessonHits.length > 0 ? (
        <section data-testid="su-lesson-results">
          <h2 className="font-heading text-lg text-navy mb-3">Lessons matching &ldquo;{q.trim()}&rdquo;</h2>
          <div className="space-y-2">
            {lessonHits.map((l) => (
              <button
                key={`${l.course_id}-${l.lesson_id}`}
                data-testid={`su-lesson-hit-${l.lesson_id}`}
                onClick={() => nav(`/app/support-university/${l.course_id}?lesson=${l.lesson_id}`)}
                className="w-full text-left flex items-center gap-3 bg-white rounded-2xl p-4 card-soft hover:-translate-y-0.5 transition-smooth"
              >
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
                  <GraduationCap size={16} className="text-gold" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-navy truncate">{l.lesson_title}</span>
                  <span className="block text-xs text-navy/45 truncate">{l.course_title}{l.objective ? ` · ${l.objective}` : ''}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* Full catalog */}
      <section>
        <h2 className="font-heading text-lg text-navy mb-3">{hasFilter ? 'Results' : 'All courses'}</h2>
        {catalog.length === 0 ? (
          <p className="text-navy/50 text-sm">No courses match your search.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {catalog.map((c) => <CourseCard key={c.id} c={c} testid={`su-catalog-${c.id}`} onOpen={() => openCourse(c)} />)}
          </div>
        )}
      </section>
      </>
      )}
    </div>
  );
}
